import React, { useEffect, useState } from 'react';
import { studioApiService } from '../../services/studioApiService';

interface S3DirectRow {
  id: string;
  row_id: string;
  product_id?: string;
  product_link?: string;
  name: string;
  brand: string;
  marketplace: string;
  color: string;
  price: string;
  discount_price: string;
  available_sizes: string;
  description: string;
  gender: string;
  related_products: string; // Comma-separated product IDs
  dbStatus: 'pending' | 'submitting' | 'submitted' | 'error';
  error?: string;
}

function autoClusterRelatedRows(rawRows: S3DirectRow[]): S3DirectRow[] {
  const clusterMap = new Map<string, string[]>();

  rawRows.forEach((r) => {
    const rId = r.row_id.trim();
    if (!rId) return;

    // Strict grouping by Cluster Keys in the related products column (e.g. 4026, 4037, 4043)
    const rawRel = (r.related_products || '').trim();
    if (rawRel) {
      rawRel.split(',').forEach((k) => {
        k = k.trim();
        if (k) {
          const list = clusterMap.get(k) || [];
          if (!list.includes(rId)) list.push(rId);
          clusterMap.set(k, list);
        }
      });
    }
  });

  const adj = new Map<string, Set<string>>();
  rawRows.forEach((r) => {
    const rId = r.row_id.trim();
    if (rId && !adj.has(rId)) adj.set(rId, new Set());
  });

  clusterMap.forEach((members) => {
    for (let i = 0; i < members.length; i++) {
      for (let j = 0; j < members.length; j++) {
        if (members[i] !== members[j]) {
          adj.get(members[i])?.add(members[j]);
          adj.get(members[j])?.add(members[i]);
        }
      }
    }
  });

  const visited = new Set<string>();
  const rowToCluster = new Map<string, string[]>();

  rawRows.forEach((r) => {
    const rId = r.row_id.trim();
    if (!rId || visited.has(rId)) return;

    const comp: string[] = [];
    const queue = [rId];
    visited.add(rId);

    while (queue.length > 0) {
      const curr = queue.shift();
      if (curr === undefined) break;
      comp.push(curr);
      for (const neighbor of adj.get(curr) || []) {
        if (!visited.has(neighbor)) {
          visited.add(neighbor);
          queue.push(neighbor);
        }
      }
    }

    comp.forEach((id) => {
      rowToCluster.set(id, comp);
    });
  });

  return rawRows.map((r) => {
    const rId = r.row_id.trim();
    const cluster = rowToCluster.get(rId);
    if (!cluster || cluster.length <= 1) {
      return {
        ...r,
        related_products: '',
      };
    }
    const siblings = cluster
      .filter((id) => id !== rId)
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

    return {
      ...r,
      related_products: siblings.join(', '),
    };
  });
}

export const DirectS3DbUpload: React.FC = () => {
  const [isSyncing, setIsSyncing] = useState<boolean>(false);
  const [syncStatusMsg, setSyncStatusMsg] = useState<string | null>(null);
  const createEmptyRow = (): S3DirectRow => ({
    id: crypto.randomUUID(),
    row_id: '',
    name: '',
    brand: 'Slayd Originals',
    marketplace: 'Slayd',
    color: '',
    price: '',
    discount_price: '',
    available_sizes: 'XS, S, M, L, XL',
    description: '',
    gender: 'Women',
    related_products: '',
    dbStatus: 'pending',
  });

  const [rows, setRows] = useState<S3DirectRow[]>([createEmptyRow()]);

  // Always clear cached rows on fresh page land / reload
  useEffect(() => {
    try {
      localStorage.removeItem('slayd_direct_s3_rows');
    } catch {
      // ignore
    }
  }, []);

  const handleAddRow = (): void => {
    setRows((prev) => [...prev, createEmptyRow()]);
  };

  const handleClearAll = (): void => {
    if (window.confirm('Are you sure you want to clear all product rows?')) {
      setRows([createEmptyRow()]);
      try {
        localStorage.removeItem('slayd_direct_s3_rows');
      } catch {
        // ignore
      }
    }
  };

  const handleRemoveRow = (id: string): void => {
    setRows((prev) => prev.filter((r) => r.id !== id));
  };

  const updateRow = (id: string, updates: Partial<S3DirectRow>): void => {
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, ...updates } : r))
    );
  };

  // ── Google Sheets Sync Handler ──
  const handleSyncGoogleSheet = async (): Promise<void> => {
    setIsSyncing(true);
    setSyncStatusMsg('Fetching available products from Google Sheet...');
    try {
      const data = await studioApiService.syncGoogleSheet();
      if (data.rows && data.rows.length > 0) {
        const mappedRows: S3DirectRow[] = data.rows.map((r) => ({
          ...r,
          available_sizes: (r.available_sizes || 'XS, S, M, L, XL')
            .split(',')
            .map((s) => s.trim().toUpperCase())
            .filter(Boolean)
            .join(', '),
          dbStatus: 'pending' as const,
        }));
        const clusteredRows = autoClusterRelatedRows(mappedRows);
        setRows(clusteredRows);
        setSyncStatusMsg(`✓ Synced ${data.rows.length} available products!`);
        setTimeout(() => setSyncStatusMsg(null), 4000);
      } else {
        setSyncStatusMsg('No available products found (is_available = TRUE)');
        setTimeout(() => setSyncStatusMsg(null), 4000);
      }
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : 'Google Sheet Sync Error';
      setSyncStatusMsg(`Sync error: ${msg}`);
      alert(`Google Sheet Sync Error: ${msg}`);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleSubmitRow = async (row: S3DirectRow): Promise<void> => {
    if (!row.name.trim()) {
      alert('Please enter a Product Name.');
      return;
    }
    if (!row.row_id.trim()) {
      alert('Please enter a Row ID.');
      return;
    }

    const parsedPrice = parseFloat(row.price) || 0;
    const parsedDiscountPrice = parseFloat(row.discount_price) || parsedPrice;

    // Clean sizes
    const sizesList = row.available_sizes
      .split(',')
      .map((s) => s.trim().toUpperCase())
      .filter(Boolean);
    const formattedSizes = sizesList.join(', ');

    // Clean related products
    const relatedList = row.related_products
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    const prodLink =
      row.product_link && row.product_link.trim().startsWith('http')
        ? row.product_link.trim()
        : `https://slayd.in/internal/feed/${row.row_id.trim()}`;
    const dPrice = parseFloat(parsedDiscountPrice.toFixed(2));

    const payload = {
      // CamelCase keys (Backend DRF requirement)
      productLink: prodLink,
      discountPrice: dPrice,
      relatedProducts: relatedList,
      availableSizes: formattedSizes,
      isSlaydOriginals: true,
      isAvailable: true,

      // Snake_case keys (Compatibility)
      product_link: prodLink,
      discount_price: dPrice,
      related_products: relatedList,
      available_sizes: formattedSizes,
      is_slayd_originals: true,
      is_available: true,

      // Common fields
      row_id: row.row_id.trim(),
      product_id: row.row_id.trim(),
      productId: row.row_id.trim(),
      title: row.name.trim(),
      name: row.name.trim(),
      brand: row.brand.trim() || 'Slayd Originals',
      marketplace: 'Slayd',
      price: parsedPrice,
      mrp: dPrice,
      color: row.color.trim(),
      gender: 'Women',
      description: row.description.trim(),
      slayd_originals_color: row.color.trim(),
      slayd_originals_size: formattedSizes,
      slayd_originals_description: row.description.trim(),
    };

    updateRow(row.id, { dbStatus: 'submitting', error: undefined });

    try {
      const res = await studioApiService.createProductInDB(payload);
      const assignedId =
        res?.id || res?.product?.id || res?.product_id || row.product_id;
      const finalIdStr = assignedId ? String(assignedId) : row.product_id;
      updateRow(row.id, {
        dbStatus: 'submitted',
        product_id: finalIdStr,
      });

      if (finalIdStr && row.row_id.trim()) {
        studioApiService
          .updateSheetProductIds([
            { row_id: row.row_id.trim(), product_id: finalIdStr },
          ])
          .then(() =>
            setSyncStatusMsg(`✓ Updated product ID in Google Sheets!`)
          )
          .catch((err) => console.warn('Failed to update product ID:', err));
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Upload failed';
      updateRow(row.id, { dbStatus: 'error', error: msg });
    }
  };

  const handleSubmitAll = async (): Promise<void> => {
    const readyRows = rows.filter(
      (r) => r.row_id.trim() && r.name.trim() && r.dbStatus !== 'submitted'
    );
    if (readyRows.length === 0) {
      return;
    }

    setSyncStatusMsg(
      `Resetting previous Slayd Originals to unavailable in DB...`
    );
    try {
      await studioApiService.markUnavailableByBrand('Slayd Originals', 'Slayd');
      await studioApiService.markUnavailableByBrand('Slayd Originals', '');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      console.warn('[Studio] Failed to reset brand availability:', msg);
    }

    setSyncStatusMsg(`Submitting ${readyRows.length} active products to DB...`);
    const sheetUpdates: { row_id: string; product_id: string }[] = [];

    for (const r of readyRows) {
      try {
        const parsedPrice = parseFloat(r.price) || 0;
        const parsedDiscountPrice = parseFloat(r.discount_price) || parsedPrice;
        const sizesList = r.available_sizes
          .split(',')
          .map((s) => s.trim().toUpperCase())
          .filter(Boolean);
        const formattedSizes = sizesList.join(', ');
        const relatedList = r.related_products
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
        const prodLink =
          r.product_link && r.product_link.trim().startsWith('http')
            ? r.product_link.trim()
            : `https://slayd.in/internal/feed/${r.row_id.trim()}`;
        const dPrice = parseFloat(parsedDiscountPrice.toFixed(2));

        const payload = {
          productLink: prodLink,
          discountPrice: dPrice,
          relatedProducts: relatedList,
          availableSizes: formattedSizes,
          isSlaydOriginals: true,
          isAvailable: true,
          product_link: prodLink,
          discount_price: dPrice,
          related_products: relatedList,
          available_sizes: formattedSizes,
          is_slayd_originals: true,
          is_available: true,
          row_id: r.row_id.trim(),
          product_id: r.row_id.trim(),
          productId: r.row_id.trim(),
          title: r.name.trim(),
          name: r.name.trim(),
          brand: r.brand.trim() || 'Slayd Originals',
          marketplace: 'Slayd',
          price: parsedPrice,
          mrp: dPrice,
          color: r.color.trim(),
          gender: 'Women',
          description: r.description.trim(),
          slayd_originals_color: r.color.trim(),
          slayd_originals_size: formattedSizes,
          slayd_originals_description: r.description.trim(),
        };

        updateRow(r.id, { dbStatus: 'submitting', error: undefined });
        const res = await studioApiService.createProductInDB(payload);
        const assignedId =
          res?.id || res?.product?.id || res?.product_id || r.product_id;
        const finalIdStr = assignedId ? String(assignedId) : r.product_id;

        updateRow(r.id, {
          dbStatus: 'submitted',
          product_id: finalIdStr,
        });

        if (finalIdStr && r.row_id.trim()) {
          sheetUpdates.push({
            row_id: r.row_id.trim(),
            product_id: finalIdStr,
          });
        }
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'Upload failed';
        updateRow(r.id, { dbStatus: 'error', error: msg });
      }
    }

    if (sheetUpdates.length > 0) {
      setSyncStatusMsg(
        `Updating Google Sheets with ${sheetUpdates.length} product IDs...`
      );
      try {
        await studioApiService.updateSheetProductIds(sheetUpdates);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : String(err);
        console.warn('Sheet update error:', msg);
      }

      // ── Auto-Link Related Products in Django DB ──
      setSyncStatusMsg(`Linking related product groups in DB...`);
      const rowIdToDbIdMap: Record<string, string> = {};
      sheetUpdates.forEach((item) => {
        rowIdToDbIdMap[item.row_id.trim()] = item.product_id.trim();
      });

      const linkedGroupKeys = new Set<string>();
      for (const r of readyRows) {
        const myDbId = rowIdToDbIdMap[r.row_id.trim()] || r.product_id;
        if (!myDbId) continue;

        const relRowIds = (r.related_products || '')
          .split(',')
          .map((s: string) => s.trim())
          .filter(Boolean);
        if (relRowIds.length > 0) {
          const groupDbIds: number[] = [parseInt(myDbId, 10)];
          relRowIds.forEach((relRowId: string) => {
            const relDbId = rowIdToDbIdMap[relRowId];
            if (relDbId) groupDbIds.push(parseInt(relDbId, 10));
          });

          if (groupDbIds.length > 1) {
            const groupKey = [...groupDbIds].sort((a, b) => a - b).join('-');
            if (!linkedGroupKeys.has(groupKey)) {
              linkedGroupKeys.add(groupKey);
              try {
                await studioApiService.linkRelatedProducts(groupDbIds);
              } catch (err: unknown) {
                const msg = err instanceof Error ? err.message : String(err);
                console.warn(
                  '[Studio] Failed to link related products group:',
                  groupDbIds,
                  msg
                );
              }
            }
          }
        }
      }

      setSyncStatusMsg(
        `✓ Submitted all products, updated sheets, and linked related variants!`
      );
      setTimeout(() => setSyncStatusMsg(null), 5000);
    }
  };

  return (
    <div
      id="directS3DbPage"
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        padding: '20px',
        height: '100%',
      }}
    >
      {/* Header Controls (Fixed) */}
      <div style={{ flexShrink: 0 }}>
        <div
          className="section-label"
          style={{
            fontSize: '16px',
            marginBottom: '8px',
            color: 'var(--text)',
          }}
        >
          Direct S3 DB Upload
        </div>
        <div
          style={{
            fontSize: '12px',
            color: 'var(--text2)',
            marginBottom: '16px',
          }}
        >
          Sync available products from Google Sheet or add manually. S3 images (
          <code>slayd-originals/products-v2</code>) are auto-resolved
          server-side during upload.
        </div>

        <div
          style={{
            display: 'flex',
            gap: '10px',
            marginBottom: '16px',
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <button
            className="add-product-btn"
            style={{ width: 'auto', padding: '8px 16px' }}
            onClick={handleAddRow}
          >
            + Add Product Row
          </button>

          {/* Sync from Google Sheet Button */}
          <button
            className="row-dl-btn"
            style={{
              width: 'auto',
              padding: '8px 16px',
              background: 'var(--surface2)',
              border: '1px solid var(--border2)',
              borderRadius: '8px',
              fontSize: '12px',
              color: 'var(--text)',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              cursor: isSyncing ? 'not-allowed' : 'pointer',
              opacity: isSyncing ? 0.7 : 1,
            }}
            disabled={isSyncing}
            onClick={handleSyncGoogleSheet}
          >
            {isSyncing ? '⏳ Syncing...' : '🔄 Sync from Google Sheet'}
          </button>

          {syncStatusMsg && (
            <span
              style={{
                fontSize: '12px',
                color: syncStatusMsg.startsWith('✓')
                  ? '#4caf50'
                  : 'var(--text2)',
                fontWeight: 500,
              }}
            >
              {syncStatusMsg}
            </span>
          )}

          {/* Clear All Rows Button */}
          <button
            className="row-dl-btn"
            style={{
              width: 'auto',
              padding: '8px 16px',
              background: 'var(--surface2)',
              border: '1px solid var(--border2)',
              color: '#f44336',
              borderRadius: '8px',
              fontSize: '12px',
            }}
            onClick={handleClearAll}
          >
            🗑️ Clear All Rows
          </button>

          <button
            className="run-btn primary"
            style={{ width: 'auto', padding: '8px 20px', marginLeft: 'auto' }}
            onClick={handleSubmitAll}
          >
            Submit All Rows to DB
          </button>
        </div>
      </div>

      {/* Scrollable List Container */}
      <div
        className="panel-scroll"
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          paddingRight: '10px',
          overflowY: 'auto',
          minHeight: 0,
        }}
      >
        {rows.map((row, i) => {
          const relatedArray = row.related_products
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);

          return (
            <div
              key={row.id}
              className="product-card"
              style={{
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: '12px',
                padding: '16px',
                margin: 0,
                flexShrink: 0,
                minHeight: 'fit-content',
              }}
            >
              <div
                className="product-card-header"
                style={{ marginBottom: '14px' }}
              >
                <div className="product-card-title">
                  Product Entry #{i + 1}{' '}
                  {row.row_id ? `(Row ID: ${row.row_id})` : ''}{' '}
                  {row.product_id ? `• DB ID: ${row.product_id}` : ''}
                </div>
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
                >
                  <span className={`status-badge ${row.dbStatus}`}>
                    {row.dbStatus}
                  </span>
                  {rows.length > 1 && (
                    <button
                      className="product-card-remove"
                      onClick={() => handleRemoveRow(row.id)}
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>

              {/* Clean Single-Column Product Metadata Form */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                }}
              >
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '160px 1fr 1fr',
                    gap: '12px',
                  }}
                >
                  <div>
                    <label
                      style={{
                        fontSize: '11px',
                        color: 'var(--text2)',
                        display: 'block',
                        marginBottom: '4px',
                      }}
                    >
                      Row ID (e.g. 4015) *
                    </label>
                    <input
                      type="text"
                      className="pid-input"
                      placeholder="4015"
                      value={row.row_id}
                      style={{ width: '100%' }}
                      onChange={(e) =>
                        updateRow(row.id, { row_id: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        fontSize: '11px',
                        color: 'var(--text2)',
                        display: 'block',
                        marginBottom: '4px',
                      }}
                    >
                      Product Name *
                    </label>
                    <input
                      type="text"
                      className="pid-input"
                      placeholder="e.g. Cotton Side Dori Short Kurti"
                      value={row.name}
                      style={{ width: '100%' }}
                      onChange={(e) =>
                        updateRow(row.id, { name: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        fontSize: '11px',
                        color: 'var(--text2)',
                        display: 'block',
                        marginBottom: '4px',
                      }}
                    >
                      Brand
                    </label>
                    <input
                      type="text"
                      className="pid-input"
                      placeholder="Slayd Originals"
                      value={row.brand}
                      style={{ width: '100%' }}
                      onChange={(e) =>
                        updateRow(row.id, { brand: e.target.value })
                      }
                    />
                  </div>
                </div>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr',
                    gap: '12px',
                  }}
                >
                  <div>
                    <label
                      style={{
                        fontSize: '11px',
                        color: 'var(--text2)',
                        display: 'block',
                        marginBottom: '4px',
                      }}
                    >
                      Price (₹) *
                    </label>
                    <input
                      type="number"
                      className="pid-input"
                      placeholder="999"
                      value={row.price}
                      style={{ width: '100%' }}
                      onChange={(e) =>
                        updateRow(row.id, { price: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        fontSize: '11px',
                        color: 'var(--text2)',
                        display: 'block',
                        marginBottom: '4px',
                      }}
                    >
                      Discount Price (₹)
                    </label>
                    <input
                      type="number"
                      className="pid-input"
                      placeholder="390"
                      value={row.discount_price}
                      style={{ width: '100%' }}
                      onChange={(e) =>
                        updateRow(row.id, { discount_price: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        fontSize: '11px',
                        color: 'var(--text2)',
                        display: 'block',
                        marginBottom: '4px',
                      }}
                    >
                      Color
                    </label>
                    <input
                      type="text"
                      className="pid-input"
                      placeholder="e.g. Blush Pink"
                      value={row.color}
                      style={{ width: '100%' }}
                      onChange={(e) =>
                        updateRow(row.id, { color: e.target.value })
                      }
                    />
                  </div>
                </div>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr',
                    gap: '12px',
                  }}
                >
                  <div>
                    <label
                      style={{
                        fontSize: '11px',
                        color: 'var(--text2)',
                        display: 'block',
                        marginBottom: '4px',
                      }}
                    >
                      Available Sizes
                    </label>
                    <input
                      type="text"
                      className="pid-input"
                      placeholder="XS, S, M, L, XL"
                      value={row.available_sizes}
                      style={{ width: '100%' }}
                      onChange={(e) =>
                        updateRow(row.id, { available_sizes: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        fontSize: '11px',
                        color: 'var(--text2)',
                        display: 'block',
                        marginBottom: '4px',
                      }}
                    >
                      Gender
                    </label>
                    <input
                      type="text"
                      className="pid-input"
                      placeholder="Women"
                      value={row.gender}
                      style={{ width: '100%' }}
                      onChange={(e) =>
                        updateRow(row.id, { gender: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        fontSize: '11px',
                        color: 'var(--accent)',
                        display: 'block',
                        marginBottom: '4px',
                      }}
                    >
                      Related Product IDs
                    </label>
                    <input
                      type="text"
                      className="pid-input"
                      placeholder="e.g. 4038, 4039"
                      value={row.related_products}
                      style={{ width: '100%' }}
                      onChange={(e) =>
                        updateRow(row.id, { related_products: e.target.value })
                      }
                    />
                  </div>
                </div>

                {/* Related Product Tags Display */}
                {relatedArray.length > 0 && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      flexWrap: 'wrap',
                    }}
                  >
                    <span style={{ fontSize: '10px', color: 'var(--text3)' }}>
                      Linked Related Products:
                    </span>
                    {relatedArray.map((relId, idx) => (
                      <span
                        key={idx}
                        style={{
                          fontSize: '10px',
                          background: 'var(--accent-dim)',
                          color: 'var(--accent)',
                          border: '1px solid var(--accent)',
                          padding: '2px 8px',
                          borderRadius: '12px',
                        }}
                      >
                        ID #{relId}
                      </span>
                    ))}
                  </div>
                )}

                <div>
                  <label
                    style={{
                      fontSize: '11px',
                      color: 'var(--text2)',
                      display: 'block',
                      marginBottom: '4px',
                    }}
                  >
                    Description
                  </label>
                  <textarea
                    className="pid-input"
                    placeholder="Product description and style details..."
                    value={row.description}
                    style={{
                      width: '100%',
                      minHeight: '54px',
                      resize: 'vertical',
                    }}
                    onChange={(e) =>
                      updateRow(row.id, { description: e.target.value })
                    }
                  />
                </div>

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    marginTop: '6px',
                  }}
                >
                  <button
                    className="run-btn primary"
                    style={{ width: 'auto', padding: '8px 24px' }}
                    disabled={row.dbStatus === 'submitting'}
                    onClick={() => handleSubmitRow(row)}
                  >
                    {row.dbStatus === 'submitting'
                      ? 'Submitting...'
                      : 'Submit to Database'}
                  </button>
                </div>

                {row.error && <div className="row-error">{row.error}</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
